import { Avatar } from "antd";
import RestrictionBanner from "common/RestrictionBanner";
import RatingCells from "features/activity-log/blocks/RatingCells";
import { removeHtmlTags } from "features/cases/blocks/CommonCard/CommonCard.utils";
import CommentList from "features/comments/blocks/CommentList";
import ContactList from "features/contacts/blocks/ContactList";
import ContactRequest from "features/contacts/blocks/ContactRequest";
import MarkdownContent from "features/markdown/elements/MarkdownContent";
import ModeratedUserBox from "features/people/blocks/ModeratedUserBox";
import { isSeverelyRestricted } from "features/restrictions/utils";
import { useDataQuery } from "hooks/useData";
import { useFlag } from "hooks/useFlags";
import { useTexts } from "hooks/useTexts";
import { useUser } from "hooks/useUser";
import React from "react";
import LoadingPlaceholder from "ui/LoadingPlaceholder";
import ShareLink from "ui/ShareLink";

import UserContent from "../../../../feeds/UserContent";

import "./PersonalProfile.css";

const PersonalProfile: React.FC<{ id: number; onEditClick?: () => void }> = ({
  id,
  onEditClick,
}) => {
  const { txt } = useTexts("personal_profile");
  const people = useDataQuery(`people/`);
  const person = useDataQuery(`people/${id}/`);

  const contactsSharingEnabled = useFlag("contactsSharingEnabled");
  const contentLists = useFlag("contentLists");

  const {
    person: userPerson,
    isLoggedIn,
    isModerator,
    restrictions,
    isBlocked,
  } = useUser();

  const isOwnProfile = isLoggedIn && userPerson?.id === person?.id;

  const avatarUrl = isOwnProfile
    ? userPerson?.avatarUrl ||
      person?.avatarUrl ||
      "https://randomuser.me/api/portraits/lego/1.jpg"
    : person?.avatarUrl || "https://randomuser.me/api/portraits/lego/1.jpg";

  const canEdit = isOwnProfile;

  if (!person || !people) {
    return <LoadingPlaceholder />;
  }

  return (
    <div>
      {isOwnProfile && !isSeverelyRestricted(restrictions) && (
        <RestrictionBanner
          restrictions={restrictions}
          showAppealLink={false}
        />
      )}

      {isOwnProfile && restrictions.length > 0 && (
        <CommentList
          commentTargetType="person"
          commentTargetId={person.id}
          isModerated
          defaultExpanded
        />
      )}

      <ModeratedUserBox
        personId={id}
        moderatorMode={true}
      />

      {isModerator && <RatingCells personId={id} />}

      <div className="personal-profile">
        <Avatar
          style={{ backgroundColor: "white" }}
          size={64}
          src={avatarUrl}
          className="avatar"
        />
        <h2 className="profile-title">
          {person.firstName}{" "}
          <ShareLink
            url={`${window.location.origin}/#/people/${person.id}`}
            elementName={txt("link_to_card", "профиль")}
          />
        </h2>

        <div className="profile-info">
          <strong>{txt("about_label", "О себе:")}</strong>
        </div>
        <MarkdownContent content={removeHtmlTags(person.about || "")} />

        {contactsSharingEnabled && person.contactInformation && (
          <div className="profile-info">
            <p></p>
            <strong>
              {txt("contact_label", "Контактная информация:")}
            </strong>{" "}
            <br />
            <span>{person.contactInformation}</span>
          </div>
        )}

        {canEdit && onEditClick && (
          <button
            className="edit-button"
            onClick={onEditClick}
          >
            {txt("edit_button", "Редактировать профиль")}
          </button>
        )}

        {!isOwnProfile && contactsSharingEnabled && (
          <ContactRequest targetPersonId={person.id} />
        )}

        {isOwnProfile && contactsSharingEnabled && <ContactList />}
      </div>

      {contentLists && !(isOwnProfile && isBlocked) && (
        <UserContent
          openLinksInNewWindow={false}
          isOwnProfile={isOwnProfile}
          personId={person.id}
        />
      )}
    </div>
  );
};

export default PersonalProfile;
